import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { CloseIcon } from '../../../../assets/icons/CloseIcon';
import Button from '../../../../components/button/Button';
import DateTimePicker from '../../../../components/date-time-picker/DateTimePicker';
import DialogView from '../../../../components/dialog-view/DialogView';
import IconButton from '../../../../components/icon-button/IconButton';
import Typography from '../../../../components/typography/Typography';
import NotificationService from '../../../../services/NotificationService';
import { log } from '../../../../utils/logging/log';
import { describeWorkPermitTimeSource, WorkPermitTimes } from '../../../../utils/workPermitTimes';
import { DialogFooter, DialogHeader } from '../../../goods-view/components/Styles';

interface Props {
  open: boolean;
  defaultTimes?: WorkPermitTimes;
  onSubmit: (start: Date, end: Date) => Promise<void>;
  handleClose: () => void;
}

const WorkPermitTimeDialog = (props: Props) => {
  const { open, defaultTimes, onSubmit, handleClose } = props;
  const [start, setStart] = useState<Dayjs | null>(null);
  const [end, setEnd] = useState<Dayjs | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStart(defaultTimes?.start ? dayjs(defaultTimes.start) : null);
    setEnd(defaultTimes?.end ? dayjs(defaultTimes.end) : null);
  }, [open, defaultTimes]);

  const isInvalid = !start || !end || !start.isValid() || !end.isValid() || !end.isAfter(start);

  const handleSubmit = useCallback(async () => {
    if (isInvalid || !start || !end) return;

    setIsSubmitting(true);
    try {
      await onSubmit(start.toDate(), end.toDate());
      handleClose();
    } catch (error) {
      log(error);
      NotificationService.error('Kunne ikke rette tiden på arbejdstilladelsen');
    } finally {
      setIsSubmitting(false);
    }
  }, [end, handleClose, isInvalid, onSubmit, start]);

  if (!open) return null;

  return (
    <DialogView handleClose={handleClose}>
      <Container>
        <StyledDialogHeader>
          <Typography variant="h4" fontWeight="bold">
            Ret tid på arbejdstilladelsen
          </Typography>
          <IconButton variant="outlined" onClick={handleClose}>
            <CloseIcon size="18px" />
          </IconButton>
        </StyledDialogHeader>

        <Content>
          <DateTimePicker label="Start" value={start} onChange={(value) => setStart(value)} />
          <DateTimePicker
            label="Slut"
            value={end}
            onChange={(value) => setEnd(value)}
            minDateTime={start ?? undefined}
          />
          <Typography color="greyedOut" fontStyle="oblique">
            {defaultTimes
              ? describeWorkPermitTimeSource(defaultTimes.source)
              : 'Hovedopgaven har ingen bookinger, planlagt ankomst eller datoer. Angiv tiden manuelt.'}
          </Typography>
        </Content>

        <DialogFooter>
          <Buttons>
            <Button variant="secondary" onClick={handleClose}>
              Fortryd
            </Button>
            <Button onClick={handleSubmit} disabled={isInvalid || isSubmitting} isLoading={isSubmitting}>
              Gem tid
            </Button>
          </Buttons>
        </DialogFooter>
      </Container>
    </DialogView>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  width: 85vw;
  max-width: 560px;
  box-sizing: border-box;
`;

const StyledDialogHeader = styled(DialogHeader)`
  width: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  padding: 24px 0;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 12px;
  width: 100%;
`;

export default WorkPermitTimeDialog;
